import { Configuration, EventType, LogLevel, PublicClientApplication } from "@azure/msal-browser";

export const createMsalInstance = async (environment: string, clientId: string, tenantId: string, redirectUri: string, nonce?: string): Promise<PublicClientApplication> => {
    // Normalize the URLs by removing "www."
    const normalizeUrl = (url: string) => {
        if (url) {
            const urlObj = new URL(url);
            const hostname = urlObj.hostname.replace(/^www\./, '');
            return `${urlObj.protocol}//${hostname}${urlObj.port ? ':' + urlObj.port : ''}`;
        }
        return '';
    };

    const currentOrigin = normalizeUrl(window?.location?.origin);
    const normalizedRedirectUri = normalizeUrl(redirectUri);

    let azureRedirectUrl = redirectUri;
    // Check if the normalized values match
    if (currentOrigin === normalizedRedirectUri) {
        azureRedirectUrl = window?.location?.origin;
    }

    const config: Configuration = {
        auth: {
            clientId: clientId,
            authority: `https://login.microsoftonline.com/${tenantId}`,
            redirectUri: azureRedirectUrl?.endsWith('/') ? azureRedirectUrl.slice(0, -1) : azureRedirectUrl,
            postLogoutRedirectUri: "/",
        },
        cache: {
            storeAuthStateInCookie: true,
            secureCookies: environment?.toLowerCase() !== 'local',
            cacheLocation: 'localStorage',
            temporaryCacheLocation: "localStorage",
            claimsBasedCachingEnabled: true
        },
        system: {
            loggerOptions: {
                logLevel: LogLevel.Verbose,
                piiLoggingEnabled: false,
                correlationId: nonce,
                loggerCallback: (level: LogLevel, message: string, containsPii: boolean) => {
                    if (containsPii) {
                        return;
                    }
                    switch (level) {
                        case LogLevel.Error:
                            environment?.toLowerCase() === 'local' && console.error(message);
                            return;
                        case LogLevel.Info:
                            environment?.toLowerCase() === 'local' && console.info(message);
                            return;
                        case LogLevel.Verbose:
                            environment?.toLowerCase() === 'local' && console.debug(message);
                            return;
                        case LogLevel.Warning:
                            environment?.toLowerCase() === 'local' && console.warn(message);
                            return;
                        case LogLevel.Trace:
                            environment?.toLowerCase() === 'local' && console.trace(message);
                            return;
                    }
                },
            },
        },
    };

    const msalInstance = new PublicClientApplication(config);

    // Await the initialization of the MSAL instance
    await msalInstance.initialize();

    msalInstance.addEventCallback((event) => {
        if (event.eventType === EventType.LOGIN_FAILURE) {
            console.error("Login failed:", event?.error);
        }
        else if (event?.error) {
            console.error("Error:", event?.error);
        }
    });

    return msalInstance; // Return the initialized instance
};